import { ThemeProvider, createTheme } from '@mui/material/styles';
import React, { ReactNode } from 'react';

import { ICommonProperties } from '../../common/properties/ICommonProperties';
import { Colors } from '../../view/design/color/Colors';

export interface IApplicationThemeProperties extends ICommonProperties {
  children: ReactNode;
  isFlip?: boolean;
}

export default function ApplicationTheme({ isFlip = false, children }: IApplicationThemeProperties) {
  const theme = createProjectTheme(isFlip);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

const createProjectTheme = (isFlip: boolean) => {
  const theme = createTheme({
    palette: {
      mode: Colors.paletteMode,
      background: {
        paper: Colors.bg,
        default: Colors.bg,
      },
      primary: { main: Colors.primary },
      secondary: { main: Colors.primary },
      info: { main: Colors.primary },
      error: { main: Colors.errorLight },
      success: { main: Colors.success },
      warning: { main: Colors.warning },
      text: {
        primary: Colors.textPrimary,
        secondary: Colors.textSecondary,
        disabled: Colors.textDisabled,
      },
      divider: Colors.border,
    },
    typography: {
      fontFamily: ['Late', 'Roboto', 'Arial', 'sans-serif'].join(','),
      button: { textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollbarColor: `${Colors.scrollbarThumb} ${Colors.scrollbarTrack}`,
          },
          '*::-webkit-scrollbar': {
            width: '14px',
            height: '14px',
          },
          '*::-webkit-scrollbar-track': {
            background: Colors.scrollbarTrack,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: Colors.scrollbarThumb,
            borderRadius: '8px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: Colors.scrollbarThumbHover,
          },
          'input, textarea': {
            caretColor: Colors.textPrimary,
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          'input::selection, textarea::selection': {
            backgroundColor: Colors.inputSelectionBg,
            color: Colors.inputSelectionText,
          },
          'input::-moz-selection, textarea::-moz-selection': {
            backgroundColor: Colors.inputSelectionBg,
            color: Colors.inputSelectionText,
          },
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active': {
            WebkitBoxShadow: `0 0 0 1000px ${Colors.bg} inset !important`,
            WebkitTextFillColor: `${Colors.textPrimary} !important`,
            caretColor: `${Colors.textPrimary}`,
            transition: 'background-color 5000s ease-in-out 0s',
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              background: Colors.btnAccent,
              color: Colors.textOnPrimary,
              '&:hover': {
                background: Colors.btnAccentHover,
              },
            },
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              background: Colors.btnAccentBg,
              color: Colors.btnAccent,
              '&:hover': {
                background: Colors.btnAccentBgHover,
              },
            },
          },
          {
            props: { variant: 'contained', color: 'success' },
            style: {
              background: Colors.withOpacity(Colors.success, 0.7),
              color: Colors.textPrimary,
              '&:hover': {
                background: Colors.withOpacity(Colors.success, 0.85),
              },
            },
          },
          {
            props: { variant: 'contained', color: 'error' },
            style: {
              background: Colors.withOpacity(Colors.error, 0.6),
              color: Colors.textPrimary,
              '&:hover': {
                background: Colors.withOpacity(Colors.error, 0.75),
              },
            },
          },
          {
            props: { variant: 'outlined', color: 'primary' },
            style: {
              borderColor: Colors.btnAccentBorder,
              color: Colors.btnOutlineText,
              '&:hover': {
                borderColor: Colors.btnAccentBorderHover,
                background: Colors.btnAccentOutlineHover,
              },
            },
          },
        ],
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: Colors.bgElevated,
            border: `1px solid ${Colors.border}`,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: Colors.bgElevated,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            width: 'fit-content !important',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              color: Colors.inputErrorBorder,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              color: Colors.inputErrorBorder,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.inputErrorBorder,
              borderWidth: '2px',
            },
            '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.inputErrorBorder,
            },
            '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.inputErrorBorder,
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            '&.Mui-error:before': {
              borderBottomColor: Colors.inputErrorBorder,
            },
            '&.Mui-error:after': {
              borderBottomColor: Colors.inputErrorBorder,
            },
            '&.Mui-error:hover:not(.Mui-disabled):before': {
              borderBottomColor: Colors.inputErrorBorder,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            '&.Mui-error:before': {
              borderBottomColor: Colors.inputErrorBorder,
            },
            '&.Mui-error:after': {
              borderBottomColor: Colors.inputErrorBorder,
            },
            '&.Mui-error:hover:not(.Mui-disabled):before': {
              borderBottomColor: Colors.inputErrorBorder,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            '&:last-child': {
              paddingRight: '20px',
            },
          },
          paddingNone: {
            '&:last-child': {
              paddingRight: '20px',
            },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            '.MuiDialogTitle-root + &': {
              paddingTop: '10px',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderColor: Colors.border,
          },
        },
      },
    },
  });

  return theme;
};
